import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import {useIntl} from '@pinecast/i18n';
import type {CSS} from '@pinecast/styles';
import styled, {focusVisibleRing, useColors} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import type {XAlign, YAlign} from './floating/placement';
import {
  alignmentFromPlacement,
  placementFromPreferences,
} from './floating/placement';
import {useMotionSafeTransitionStyles} from './floating/useMotionSafeTransitionStyles';
import {getTabbable} from './helpers/tabbable';
import useToggle from './hooks/useToggle';
import IconButton from './IconButton';
import * as MenuIcons from './icons/menus';
import type {ElementConfig, LocalizedReactNode} from './types';

const TRANSITION_MS = 200;

// Matches the z-index `Layer` gives its portal, which is where this menu used
// to render.
const LAYER_Z_INDEX = 100;

const MenuWrapper = styled(
  'menu',
  (
    {
      $xAlign,
      $yAlign,
    }: {
      $xAlign: XAlign;
      $yAlign: YAlign;
    },
    designColors,
  ) => ({
    backgroundColor: designColors.contentBackground,
    borderColor: designColors.borderGrayNormal,
    borderStyle: 'solid',
    borderTopWidth: '1px',
    borderBottomWidth: '2px',
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    borderRadius: 4,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.125)',
    display: 'inline-flex',
    flexDirection: 'column',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    maxHeight: 300,
    minWidth: 200,
    overflowY: 'auto',
    padding: '4px 0',
    transformOrigin: `${$xAlign === 'left' ? '20%' : '80%'} ${
      $yAlign === 'top' ? 'bottom' : 'top'
    }`,
    // The list holds focus only until the arrow keys reach a row, or when it
    // has no row to go to. Inside its edge, where its scroll cannot cut the
    // ring off.
    ...focusVisibleRing(designColors, -2),
  }),
);

const MenuOptionRow = styled(
  'button',
  (
    {
      $active,
      $destructive,
    }: {
      $active: boolean;
      $destructive: boolean;
    },
    designColors,
  ): CSS => ({
    WebkitAppearance: 'none',
    background: $active
      ? $destructive
        ? designColors.badgeRedBackground
        : designColors.accentBlueLight
      : 'none',
    border: 0,
    borderRadius: 3,
    color: $destructive ? designColors.badgeRedText : designColors.contentText,
    cursor: 'pointer',
    flex: '0 0',
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    margin: '0 4px',
    padding: 8,
    textAlign: 'left',
    whiteSpace: 'nowrap',

    // The highlight above tracks `useListNavigation`, which moves real DOM
    // focus, so the row is already the focused element. Keep an explicit ring
    // anyway: the fill alone would be conveying focus by color. Inside the row,
    // so it meets the row's fill, which the contrast test checks: the default
    // ring on `accentBlueLight`, and a destructive row's red ring on
    // `badgeRedBackground`.
    ...focusVisibleRing(designColors, -2, $destructive ? 'danger' : 'default'),
  }),
);

export type MenuOption<T> = {
  name: LocalizedReactNode;
  slug: T;
  destructive?: boolean;
};

type Props<T> = {
  children: React.ReactNode;
  id?: string;
  /**
   * Act like a native select when the pointer leaves the list: the option the
   * pointer last highlighted stays active, and focus stays on it until the list
   * closes. By default, an option is active only while the pointer is over it,
   * and when the pointer leaves, no option is active and focus goes to the
   * list. That suits a menu of actions, where the last option the pointer
   * passed should not stay armed for the Enter key. It does not suit a choice
   * that the list only shows (`SelectCustom`).
   */
  keepActiveOnPointerLeave?: boolean;
  /**
   * The id (or ids, as `aria-labelledby` takes them) of what names the list.
   * By default it is named after the whole anchor, which suits a trigger that
   * is all the anchor holds. Pass it when the anchor holds more than the
   * trigger: a screen reader reads the text of everything in the anchor, and
   * that includes the list itself when the anchor owns it (`aria-owns`), so
   * the name would be every option (`SelectCustom`).
   */
  labelledBy?: string;
  options: Array<MenuOption<T> | null | false>;
  /**
   * What the list is to a screen reader (WCAG 4.1.2).
   *
   * - `'menu'` (the default) is a list of actions: Delete, open a form, go to a
   *   page. Its options are menu items, and the trigger says `aria-haspopup="menu"`.
   *   When `toSelect` is set, the options are menu items of a radio group, and
   *   the one that is chosen is checked.
   * - `'listbox'` is a list that shows a choice, as a `select` does
   *   (`SelectCustom`). Its options are options, and the one that is chosen is
   *   selected. The trigger says `aria-haspopup="listbox"`.
   *
   * The keyboard is the same for both.
   */
  role?: 'menu' | 'listbox';
  open: boolean;
  onSelect: (slug: T) => void;
  onClose: () => void;
  toSelect?: T;
  wrapperStyle?: React.CSSProperties;
};

const ContextMenu = <T extends string | number>(props: Props<T>) => {
  const {
    children,
    id,
    keepActiveOnPointerLeave = false,
    labelledBy,
    onClose,
    onSelect,
    open,
    options,
    role = 'menu',
    toSelect,
    wrapperStyle,
  } = props;

  // `options` carries `null`/`false` holes for conditionally-hidden rows. The
  // old keyboard handler counted them, so arrowing could land on a hole and
  // Enter would do nothing; indexing against the rendered rows fixes that.
  const items = React.useMemo(
    () => options.filter((option): option is MenuOption<T> => !!option),
    [options],
  );

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const selectedIndex = React.useMemo(() => {
    const found = items.findIndex(option => option.slug === toSelect);
    return found === -1 ? null : found;
  }, [items, toSelect]);

  const listRef = React.useRef<Array<HTMLElement | null>>([]);
  // Option names are translated nodes rather than strings, so typeahead reads
  // the rendered text off the DOM instead of the option objects.
  const labelsRef = React.useRef<Array<string | null>>([]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        onClose();
      }
    },
    [onClose],
  );

  const {context, floatingStyles, placement, refs} = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement: placementFromPreferences('left', 'bottom'),
    middleware: [offset(0), flip({padding: 8}), shift({padding: 8})],
    // The list is the floating element, so `top`/`left` positioning keeps
    // `transform` free for the open/close scale transition.
    transform: false,
    whileElementsMounted: autoUpdate,
  });

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    selectedIndex,
    focusItemOnOpen: true,
    // Its hover handling also clears the active option and moves focus to the
    // list when the pointer leaves an option, which is what
    // `keepActiveOnPointerLeave` turns off. So, then, the rows below follow the
    // pointer themselves.
    focusItemOnHover: !keepActiveOnPointerLeave,
    loop: true,
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    onMatch: setActiveIndex,
  });
  const dismiss = useDismiss(context);
  const ariaRole = useRole(context, {role});

  const {getFloatingProps, getItemProps, getReferenceProps} = useInteractions([
    listNavigation,
    typeahead,
    dismiss,
    ariaRole,
  ]);

  // The role of each row, and the state it has. A menu of actions has menu
  // items, which are not selected or checked. A menu that shows one choice has
  // the items of a radio group, and a list box has options.
  const itemRole =
    role === 'listbox'
      ? 'option'
      : toSelect === undefined
        ? 'menuitem'
        : 'menuitemradio';

  const {isMounted, styles: transitionStyles} = useMotionSafeTransitionStyles(
    context,
    {
      duration: TRANSITION_MS,
      initial: {opacity: 0, transform: 'scale(0.9)'},
      open: {opacity: 1, transform: 'scale(1)'},
    },
  );

  const {xAlign, yAlign} = alignmentFromPlacement(placement);

  // `useRole` wants to stamp its `aria-*` state on the reference, and for a list
  // box `role="combobox"`, and for a menu an `id` that would replace the
  // anchor's own, which the list is named by. Here the reference is only a positioning anchor wrapped around
  // whatever trigger the caller passed, and the trigger itself owns the
  // interactive semantics: it sets `aria-expanded` and `aria-haspopup` on
  // itself (see `IconMenuFactory` below). So the role and the ARIA are dropped
  // and only the event handlers kept. On a plain `span`, `aria-expanded` is
  // not an allowed attribute (axe `aria-allowed-attr`).
  const referenceProps = Object.fromEntries(
    Object.entries(getReferenceProps()).filter(
      ([key]) => key !== 'role' && key !== 'id' && !key.startsWith('aria-'),
    ),
  );

  // The list needs a name, and its trigger already has the right one ("Help
  // and support", "Episode options"). Unless the caller says otherwise
  // (`labelledBy`), the anchor holds nothing but the trigger, so pointing
  // `aria-labelledby` at it names the list after it.
  const anchorID = React.useId();

  return (
    <>
      <span
        id={anchorID}
        ref={refs.setReference}
        style={{
          display: 'inline-flex',
          verticalAlign: 'middle',
          ...wrapperStyle,
        }}
        {...referenceProps}
      >
        {children}
      </span>
      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <MenuWrapper
              $xAlign={xAlign}
              $yAlign={yAlign}
              ref={refs.setFloating}
              // `__forceStyle` is an inline style rather than a generated
              // class: `autoUpdate` rewrites these coordinates on every scroll
              // and resize tick, which would otherwise mint a class per frame.
              __forceStyle={{
                ...floatingStyles,
                ...transitionStyles,
                zIndex: LAYER_Z_INDEX,
              }}
              {...getFloatingProps({
                'aria-labelledby': labelledBy ?? anchorID,
                ...(id ? {id} : undefined),
              })}
            >
              {items.map((option, i) => (
                <MenuOptionRow
                  $active={activeIndex === i}
                  $destructive={option.destructive || false}
                  {...(itemRole === 'option'
                    ? {'aria-selected': selectedIndex === i}
                    : itemRole === 'menuitemradio'
                      ? {'aria-checked': selectedIndex === i}
                      : undefined)}
                  id={id ? `${id}__${String(option.slug)}` : undefined}
                  key={String(option.slug)}
                  ref={(node: HTMLElement | null) => {
                    listRef.current[i] = node;
                    labelsRef.current[i] = node?.textContent ?? null;
                  }}
                  role={itemRole}
                  tabIndex={activeIndex === i ? 0 : -1}
                  type="button"
                  {...getItemProps({
                    // The active option is the one that has focus, so this moves
                    // focus to the row as well, as the hover handling of the
                    // list does. Setting the index it already has does nothing.
                    ...(keepActiveOnPointerLeave
                      ? {onMouseMove: () => setActiveIndex(i)}
                      : undefined),
                    onClick: (e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Focus goes back to the trigger before the option runs.
                      // The row is gone once the menu closes, so a dialog that
                      // the option opens gives focus back to the trigger when
                      // it closes, and not to the top of the page.
                      const anchor = refs.domReference.current;
                      if (anchor instanceof HTMLElement) {
                        getTabbable(anchor)[0]?.focus({preventScroll: true});
                      }
                      onSelect(option.slug);
                      onClose();
                    },
                  })}
                >
                  {option.name}
                </MenuOptionRow>
              ))}
            </MenuWrapper>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default ContextMenu;

type MenuProps<T extends string | number> = {
  onSelect: Props<T>['onSelect'];
  options: Props<T>['options'];
  size?: ElementConfig<typeof IconButton>['size'];
  style?: CSS;
  // The button's accessible name. Pass one whenever several of these menus
  // appear together (say, one per table row) so each is distinguishable.
  title?: string;
  toSelect?: Props<T>['toSelect'];
};
const IconMenuFactory = function <T extends string | number>(
  Component: React.ComponentType<any>,
) {
  return (props: MenuProps<T>) => {
    const designColors = useColors(); // eslint-disable-line react-hooks/rules-of-hooks
    const {formatMessage} = useIntl(); // eslint-disable-line react-hooks/rules-of-hooks
    const [open, toggle] = useToggle(); // eslint-disable-line react-hooks/rules-of-hooks
    return (
      <ContextMenu<T>
        open={open}
        onClose={() => toggle(false)}
        onSelect={props.onSelect}
        options={props.options}
        toSelect={props.toSelect}
      >
        <IconButton
          aria-expanded={open}
          aria-haspopup="menu"
          Component={Component}
          iconProps={{
            color:
              (props.style && props.style.color && String(props.style.color)) ||
              designColors.contentText,
            size: 20,
          }}
          onClick={toggle}
          size={props.size}
          style={props.style}
          title={
            props.title ??
            formatMessage({
              description: 'Label for toggling a menu button',
              defaultMessage: 'Toggle menu',
            })
          }
        />
      </ContextMenu>
    );
  };
};

export const KebabIconMenu = IconMenuFactory(MenuIcons.Kebab);
export const MeatballIconMenu = IconMenuFactory(MenuIcons.Meatball);
